import { FormikProps } from 'formik';
import { Col, Form, Row, Typography, Flex } from 'antd';
import { memo } from 'react';

import dayjs from 'dayjs';
import {
  MDatePicker,
  MInput,
  MRadio,
  MSelect,
  MTextArea,
} from '@Components/Base';
import { OptionNum } from '@Components/types/options';

import { ResumptionRequestFormValues } from '../../types';

interface RequestDetailsSectionProps {
  formik: FormikProps<ResumptionRequestFormValues>;
  label: string;
  formApproval: boolean;
  resumingFromOptions: OptionNum[];
  resumptionWorkShiftOptions: OptionNum[];
  onResumingFromChange?: (leaveRequestId: number) => void;
  attendanceTime: string;
}

const RequestDetailsSection: React.FC<RequestDetailsSectionProps> = ({
  formik,
  formApproval,
  resumingFromOptions,
  onResumingFromChange,
  attendanceTime,
  resumptionWorkShiftOptions,
}) => {
  const {
    resumingFromLeaveRequestId: resumingFromLeaveRequestIdTouched,
    workShift: workShiftTouched,
    currentLocation: currentLocationTouched,
  } = formik.touched;
  const {
    resumingFromLeaveRequestId: resumingFromLeaveRequestIdError,
    resumingOnDate: resumingOnDateError,
    workShift: workShiftError,
    currentLocation: currentLocationError,
  } = formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          label="Resuming From (Selected Earlier Leave)"
          validateStatus={
            resumingFromLeaveRequestIdError && resumingFromLeaveRequestIdTouched
              ? 'error'
              : ''
          }
          help={
            resumingFromLeaveRequestIdError && resumingFromLeaveRequestIdTouched
              ? resumingFromLeaveRequestIdError
              : ''
          }
          required={!formApproval}
        >
          <MSelect
            value={formik.values.resumingFromLeaveRequestId}
            options={resumingFromOptions}
            onChange={(selectedValue) => {
              onResumingFromChange?.(selectedValue);
              formik.setFieldValue('resumingFromLeaveRequestId', selectedValue);
            }}
            onBlur={() => {
              formik.setFieldTouched('resumingFromLeaveRequestId', true);
            }}
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Resuming on"
          validateStatus={resumingOnDateError ? 'error' : ''}
          help={resumingOnDateError || attendanceTime}
          required={!formApproval}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="resumingOnDate"
            format="DD/MM/YYYY"
            value={
              formik.getFieldProps('resumingOnDate').value &&
              dayjs(formik.getFieldProps('resumingOnDate').value)
            }
            onChange={(value) => {
              if (value) {
                formik.setFieldValue(
                  'resumingOnDate',
                  // have to pass as format string because dayjs calls to string and can cause timezone date shift
                  dayjs(value).format('YYYY-MM-DD')
                );
              }

              if (value === null) {
                formik.setFieldValue('resumingOnDate', '');
              }
            }}
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Work Shift"
          validateStatus={workShiftTouched && workShiftError ? 'error' : ''}
          help={workShiftTouched && workShiftError ? workShiftError : ''}
          required={!formApproval}
        >
          <Col style={{ marginBottom: '8px', marginTop: '-8px' }}>
            <Typography.Text type="secondary">
              Default is Day Shift
            </Typography.Text>
          </Col>
          <Col>
            <Flex>
              {resumptionWorkShiftOptions.map((workShift) => (
                <MRadio
                  id={workShift.label}
                  value={workShift.value}
                  name="work-shift"
                  labelText={workShift.label}
                  onChange={(event) =>
                    formik.setFieldValue('workShift', event.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched('workShift', true);
                  }}
                  checked={Number(formik.values.workShift) === workShift.value}
                />
              ))}
            </Flex>
          </Col>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Current Location"
          validateStatus={
            currentLocationTouched && currentLocationError ? 'error' : ''
          }
          help={
            currentLocationTouched && currentLocationError
              ? currentLocationError
              : ''
          }
        >
          <MInput
            name="currentLocation"
            value={formik.values.currentLocation}
            onChange={(e) => {
              formik.setFieldValue('currentLocation', e.target.value);
            }}
            onBlur={() => {
              formik.setFieldTouched('currentLocation', true);
            }}
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Remarks"
          validateStatus={formik.errors.remarks ? 'error' : ''}
          help={formik.errors.remarks || ''}
        >
          <MTextArea
            name="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            rows={2}
            showCount
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default memo(RequestDetailsSection);
